import { Link } from 'react-router-dom';

import { Card, PageHeader } from '../ui';

/**
 * The privacy policy: static, public, and written from what the software
 * actually stores.
 *
 * DRAFT. Prepared for the operator's review before real users rely on it; it
 * describes the system as built (see docs/schema.md) and has not been read by
 * a lawyer. The draft marker is in this comment and in the report, not on
 * the page: a policy that calls itself a draft is a policy nobody trusts, and
 * the operator's sign-off is what removes this note.
 *
 * Static content rather than an API document because it changes with the
 * code, not with a publish step: the day Nexus stores something new is the
 * day this file is edited in the same commit.
 */
export const PRIVACY_EFFECTIVE_DATE = '25 September 2026';

export function Privacy(): React.JSX.Element {
  return (
    <>
      <PageHeader
        eyebrow="Nexus"
        title="Privacy policy"
        lead={`What Nexus stores about you, why, for how long, and who else sees it. Effective ${PRIVACY_EFFECTIVE_DATE}.`}
      />
      <Card>
        <article className="legal">
          <h2>Who we are</h2>
          <p>
            Nexus is run by WeClip (&ldquo;we&rdquo;). It is the platform on which creators
            join clipping campaigns, connect the social accounts they post from, submit the
            links to their posts, and are paid on verified views. Questions about this policy
            go to <a href="mailto:nexus@weclip.media">nexus@weclip.media</a>.
          </p>

          <h2>What we store, and why</h2>
          <ul>
            <li>
              <strong>Your account:</strong> email address, a password hash (never the
              password), display name, country, and when you signed up, confirmed your address
              and last signed in. Needed to run your account and to reach you about it.
            </li>
            <li>
              <strong>Your acceptance of the terms:</strong> which version you accepted, when,
              the exact text&rsquo;s fingerprint, and a coarse fingerprint of where it was
              accepted from. Needed so both sides can show what was agreed.
            </li>
            <li>
              <strong>Connected accounts:</strong> for each TikTok, Instagram or YouTube
              account you connect, its public handle, the platform&rsquo;s account id, the
              public numbers on it (followers, following, posts, engagement) and the checks we
              ran to confirm it is yours. If you connect with a platform sign-in, the read-only
              access token is stored encrypted. Needed to prove a post is yours and to read its
              statistics.
            </li>
            <li>
              <strong>Submissions:</strong> the links you submit, the public statistics we read
              from them on a schedule, and every check that ran on each one with what it saw.
              Needed to calculate what you are paid and to show you how.
            </li>
            <li>
              <strong>Earnings and payouts:</strong> every earning, hold, reversal and payout,
              in a ledger. Your payout destination (for example a PayPal address) is stored
              encrypted and never shown back in full. A tax reference, where one is required to
              pay you, is stored and never displayed. Needed to pay you and to keep books.
            </li>
            <li>
              <strong>Referrals:</strong> your referral code and which creators signed up with
              it. Needed to pay referral bonuses.
            </li>
            <li>
              <strong>Downloads from a campaign&rsquo;s clip library:</strong> which file you
              downloaded, when, and the IP address it was requested from. Needed because the
              material is a client&rsquo;s and the client is entitled to know where it went.
            </li>
            <li>
              <strong>Security records:</strong> session records (a hash of the session token,
              a hash of the IP address and a summary of the browser), request counts used for
              rate limiting, and an audit trail of who changed what. Needed to keep accounts
              safe and to answer disputes.
            </li>
          </ul>
          <p>Nexus stores no copy of your videos. It reads links and public numbers.</p>

          <h2>Who else sees it</h2>
          <ul>
            <li>
              <strong>Cloudflare</strong> hosts the application, its database and its email
              queue.
            </li>
            <li>
              <strong>The statistics providers</strong> we read public post and profile data
              through (RapidAPI services for TikTok and Instagram, Google&rsquo;s YouTube Data
              API) receive the public handle or link being read. They never receive your email
              address or payout details.
            </li>
            <li>
              <strong>The email provider</strong> (Resend, when configured) receives your
              address and the confirmation or reset email sent to it.
            </li>
            <li>
              <strong>The operator</strong> is notified on Telegram about signups, submissions
              waiting for review and payout batches. Those messages carry display names,
              campaign names and amounts, never passwords, tokens or payout destinations.
            </li>
            <li>
              <strong>The campaign&rsquo;s client</strong> can see the public posts submitted
              to their campaign, their verified view counts and the display names on the
              leaderboard.
            </li>
            <li>
              <strong>The payout rail</strong> you choose (for example PayPal) receives what is
              needed to send you money.
            </li>
          </ul>
          <p>We do not sell your data and we do not run advertising on it.</p>

          <h2>How long we keep it</h2>
          <p>
            Account, submission, earning and ledger records are kept for as long as your account
            exists and afterwards for as long as tax and accounting rules require, because they
            are records of money. Sessions expire after 14 days. Rate-limit counters expire
            within hours. Connection tokens are deleted when you disconnect an account or when
            the operator revokes it.
          </p>

          <h2>Your rights</h2>
          <p>
            You can read and change your display name and country on your Profile, disconnect an
            account at any time, and ask us to close your account or to send you a copy of what we
            hold by writing to <a href="mailto:nexus@weclip.media">nexus@weclip.media</a>. Where the
            law where you live gives you further rights (access, correction, erasure, portability,
            objection), you can exercise them the same way. Earnings already calculated are still
            paid after an account is closed.
          </p>

          <h2>Cookies</h2>
          <p>
            One cookie: the session, which keeps you signed in and is deleted when you sign out or
            after 14 days. Your theme choice (light, dark or system) is kept in your browser&rsquo;s
            local storage and never sent anywhere. There are no tracking or advertising cookies.
          </p>

          <h2>Changes</h2>
          <p>
            When this policy changes, the date at the top changes with it and anything new that
            we store is listed above before it is stored.
          </p>
        </article>
      </Card>
      <p className="muted">
        See also the <Link to="/terms">creator terms</Link>.
      </p>
    </>
  );
}
